import type { Input, PlayerId, State, Winner } from './types';

// The wire protocol between client and server: JSON text frames, one message per frame, discriminated
// by `type`. Lives in shared so both sides compile against the same shapes; the server validates what
// arrives (never trust the wire), the client trusts the server.
//
// Flow: the client opens a socket and sends `hello` (with the session token from an earlier visit, if
// any) and gets `welcome` with the token to keep. `queue` enters matchmaking; `start` opens a match
// with the full initial state. From then on the client sends one `input` per tick and receives
// `snapshot`s carrying `lastProcessedSeq`, the seq of its last input the server has applied: the
// client resets to the snapshot and replays every own input with a higher seq. `end` closes the match.
// A `start` with `resumed: true` arrives after a reconnect while the match was still running; the
// client restarts its prediction from that state and its seq counter from 0, the server has forgotten
// the old inputs.

/** Bumped for incompatible state or simulation changes. 10: random pickup locations and zone weights. */
export const PROTOCOL_VERSION = 10;

export type OpponentKind = 'human' | 'bot';

/** Why a match ended: the simulation decided, or a player left (or stayed away past the grace). */
export type EndReason = 'match' | 'forfeit';

export type ErrorCode = 'version' | 'bad-message' | 'rate-limit' | 'hello-first' | 'not-allowed';

/** WebSocket close codes the server uses beyond the standard ones; the client tells them apart. */
export const CLOSE_CODES = {
  /** a newer socket took over this session (another tab, or a reconnect that won): do not reconnect */
  replaced: 4000,
  /** the client speaks another protocol version: reload, do not reconnect */
  version: 4001,
  /** no hello in time */
  helloTimeout: 4002,
  /** RFC 6455 policy violation, used for the rate limit */
  policy: 1008,
} as const;

/** The parts of a State that never change during a match: sent once with `start`, left out of snapshots. */
export type StaticState = Pick<State, 'map' | 'pillars' | 'spawnSpots' | 'arenaSetup'>;

/** Everything else: what a snapshot carries. */
export type Snapshot = Omit<State, keyof StaticState>;

export function toSnapshot(s: State): Snapshot {
  return {
    tick: s.tick,
    rng: s.rng,
    phase: s.phase,
    phaseEndsAtTick: s.phaseEndsAtTick,
    transitionTicks: s.transitionTicks,
    winner: s.winner,
    outcome: s.outcome,
    chess: s.chess,
    players: s.players,
    crates: s.crates,
    npcBoxes: s.npcBoxes,
    npcs: s.npcs,
    nextNpcId: s.nextNpcId,
    nextNpcBoxTick: s.nextNpcBoxTick,
    pickups: s.pickups,
    projectiles: s.projectiles,
    bursts: s.bursts,
    beams: s.beams,
    blasts: s.blasts,
    spawnPlan: s.spawnPlan,
  };
}

export function staticsOf(s: State): StaticState {
  return { arenaSetup: s.arenaSetup, map: s.map, pillars: s.pillars, spawnSpots: s.spawnSpots };
}

export function fromSnapshot(snapshot: Snapshot, statics: StaticState): State {
  return { ...snapshot, arenaSetup: statics.arenaSetup, map: statics.map, pillars: statics.pillars, spawnSpots: statics.spawnSpots };
}

// --- client → server ----------------------------------------------------------

export interface HelloMessage {
  type: 'hello';
  version: number;
  /** the token from the last `welcome`, to pick a running match back up */
  token?: string;
}

export interface QueueMessage {
  type: 'queue';
}

/** Leaves the queue, or gives up the running match. */
export interface LeaveMessage {
  type: 'leave';
}

export interface InputMessage {
  type: 'input';
  input: Input;
}

export interface PingMessage {
  type: 'ping';
  /** client clock, echoed back */
  t: number;
}

export type ClientMessage = HelloMessage | QueueMessage | LeaveMessage | InputMessage | PingMessage;

// --- server → client ----------------------------------------------------------

export interface WelcomeMessage {
  type: 'welcome';
  token: string;
}

export interface QueuedMessage {
  type: 'queued';
  /** 1-based place in the queue */
  position: number;
}

export interface StartMessage {
  type: 'start';
  roomId: string;
  playerId: PlayerId;
  opponent: OpponentKind;
  seed: number;
  state: State;
  /** the match was already running: a reconnect */
  resumed: boolean;
}

export interface SnapshotMessage {
  type: 'snapshot';
  snapshot: Snapshot;
  /** seq of the receiver's last input the server has applied; -1 before the first */
  lastProcessedSeq: number;
}

export interface EndMessage {
  type: 'end';
  winner: Winner;
  reason: EndReason;
  snapshot: Snapshot;
}

export interface PongMessage {
  type: 'pong';
  t: number;
}

export interface ErrorMessage {
  type: 'error';
  code: ErrorCode;
  message: string;
}

export type ServerMessage = WelcomeMessage | QueuedMessage | StartMessage | SnapshotMessage | EndMessage | PongMessage | ErrorMessage;
